import { observer } from 'mobx-react-lite'
import {
  Alert,
  Button,
  Divider,
  Label,
  TextareaWithLabel,
  TextFieldWithLabel,
  UploadDoc,
} from 'shared/ui'
import { SelectField } from 'shared/ui/Select/SelectField/SelectField'
import { LINK_RCS_COMPLIANCE_GUIDE } from 'shared/constants/links'
import { UploadedFileCard } from 'entities/Attachment'
import { PageDescription } from 'pages/settings/ui/PageDescription/PageDescription'
import { NextButton } from 'pages/settings/ui/NextButton/NextButton'
import { LearnMoreLink } from 'pages/settings/ui/LearnMoreLink/LearnMoreLink'
import { useRcsWizardContext } from 'pages/settings/pages/rcsV2/context/rcsWizardContext'
import { RcsWizardSteps } from 'pages/settings/pages/rcsV2/route/type'
import { openRcsExampleModal } from 'pages/settings/pages/rcsV2/ui'

import styles from './styles.module.scss'

const HAS_SHORTCODE_ITEMS = [
  { id: 'yes', label: 'Yes' },
  { id: 'no', label: 'No' },
]

export const UseCase = observer(() => {
  const {
    nextStepClick,
    saving,
    isUsFlow,
    useCaseStore: {
      description,
      descriptionError,
      video,
      videoUrl,
      videoError,
      videoLoading,
      descriptionMinLength,
      descriptionMaxLength,
      isNextDisabled,
      setDescription,
      setVideo,
      removeVideo,
      clearVideoError,
      useCase,
      useCaseError,
      useCaseOptions,
      useCasesLoading,
      setUseCase,
      loadUseCases,
      monthlyTraffic,
      monthlyTrafficError,
      setMonthlyTraffic,
      validateMonthlyTraffic,
      hasShortcodeTraffic,
      hasShortcodeTrafficError,
      isShortcodeFlow,
      setHasShortcodeTraffic,
      shortcode,
      shortcodeError,
      setShortcode,
      validateShortcode,
      shortcodeTraffic,
      shortcodeTrafficError,
      setShortcodeTraffic,
      validateShortcodeTraffic,
      trafficVolume,
      trafficVolumeError,
      setTrafficVolume,
      validateTrafficVolume,
    },
  } = useRcsWizardContext()

  const onNext = () => nextStepClick(RcsWizardSteps.useCase)

  const showDetails = !isUsFlow || Boolean(useCase)

  return (
    <div className={styles.wrap}>
      <PageDescription>
        Select the campaign use case that best describes the type of messaging you plan to send. Add
        a description of your specific texting use case.
        <LearnMoreLink href={LINK_RCS_COMPLIANCE_GUIDE}>Learn more</LearnMoreLink>
      </PageDescription>

      {isUsFlow && (
        <SelectField
          className={styles.useCaseSelect}
          size='medium'
          labelProps={{ label: { text: 'Use case' } }}
          placeholder='Select'
          items={useCaseOptions}
          loading={useCasesLoading}
          value={useCase}
          setValue={setUseCase}
          error={useCaseError}
          dropdownProps={{ onToggle: (open) => open && loadUseCases() }}
        />
      )}

      {showDetails && (
        <>
          <TextareaWithLabel
            className={isUsFlow ? styles.descriptionFieldCompact : styles.descriptionField}
            classNameTextarea={styles.fieldText}
            value={description}
            onChange={setDescription}
            error={descriptionError}
            limit={descriptionMaxLength}
            minLimit={descriptionMinLength}
            errorType='bottomText'
            name='description'
            placeholder='Add a description of how the selected use case applies to your business'
            labelProps={{
              marginBottom: 0,
              label: {
                text: 'Use case description',
                rightNode: (
                  <Button
                    onClick={() => openRcsExampleModal('useCaseDescription')}
                    text='See example'
                    size='small'
                    typeBtn='informative'
                  />
                ),
              },
            }}
          />

          <Divider
            sx={{ marginTop: '35px', marginBottom: '28px', borderColor: 'var(--black-10)' }}
            orientation='horizontal'
          />

          <Label
            marginBottom={4}
            questionTooltipProps={{
              type: 'description',
              label:
                'Please provide a video that demonstrates your RCS sender in action. The video should show your sender’s core functionality, including both transactional and promotional messaging, and must include a functioning opt-out process (such as sending ‘STOP’).',
            }}
            label={{
              text: 'Use case video',
              rightNode: (
                <Button
                  onClick={() => openRcsExampleModal('useCaseVideo')}
                  text='See example'
                  size='small'
                  typeBtn='informative'
                />
              ),
            }}
          />

          {videoError && (
            <Alert
              item={{ title: videoError.title, desc: videoError.desc, type: 'error' }}
              onClose={clearVideoError}
              marginBottom={12}
            />
          )}

          <UploadDoc
            uploadTypeText='Acceptable file types: mp4, mov up to 100 MB'
            onChange={setVideo}
          />

          {(video || videoUrl) && (
            <UploadedFileCard
              file={video ?? undefined}
              url={videoUrl}
              loading={videoLoading}
              type='video'
              onRemove={removeVideo}
            />
          )}
        </>
      )}

      {isUsFlow && showDetails && (
        <div className={styles.usFields}>
          <TextFieldWithLabel
            marginBottom={0}
            errorBorder={Boolean(monthlyTrafficError)}
            errorMessage={monthlyTrafficError}
            labelProps={{ label: { text: 'Monthly organic website traffic' } }}
            textFieldProps={{
              size: 'medium',
              value: monthlyTraffic,
              onChange: setMonthlyTraffic,
              onBlur: validateMonthlyTraffic,
              InputProps: { placeholder: 'e.g. 5,000' },
            }}
          />

          <SelectField
            size='medium'
            withSearch={false}
            labelProps={{ label: { text: 'Does this use case have existing short code traffic?' } }}
            placeholder='Choose an option'
            items={HAS_SHORTCODE_ITEMS}
            value={hasShortcodeTraffic}
            setValue={setHasShortcodeTraffic}
            error={hasShortcodeTrafficError}
          />

          {isShortcodeFlow && (
            <>
              <TextFieldWithLabel
                marginBottom={0}
                errorBorder={Boolean(shortcodeError)}
                errorMessage={shortcodeError}
                labelProps={{
                  label: { text: 'Provide the existing short code associated with this use case' },
                }}
                textFieldProps={{
                  size: 'medium',
                  value: shortcode,
                  onChange: setShortcode,
                  onBlur: validateShortcode,
                  InputProps: { placeholder: 'e.g. 12345, 67890' },
                }}
              />

              <TextFieldWithLabel
                marginBottom={0}
                errorBorder={Boolean(shortcodeTrafficError)}
                errorMessage={shortcodeTrafficError}
                labelProps={{
                  label: {
                    text: 'Estimated monthly traffic volume for the existing short code number',
                  },
                }}
                textFieldProps={{
                  size: 'medium',
                  value: shortcodeTraffic,
                  onChange: setShortcodeTraffic,
                  onBlur: validateShortcodeTraffic,
                  InputProps: { placeholder: 'e.g. 10,000' },
                }}
              />
            </>
          )}

          <TextFieldWithLabel
            marginBottom={0}
            errorBorder={Boolean(trafficVolumeError)}
            errorMessage={trafficVolumeError}
            labelProps={{ label: { text: 'Expected monthly RCS sender traffic volume' } }}
            textFieldProps={{
              size: 'medium',
              value: trafficVolume,
              onChange: setTrafficVolume,
              onBlur: validateTrafficVolume,
              InputProps: { placeholder: 'e.g. 25,000' },
            }}
          />
        </div>
      )}

      <NextButton
        text='Next'
        disabled={isNextDisabled || saving}
        loading={saving}
        onClick={onNext}
      />
    </div>
  )
})
